import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Package, MapPin, Thermometer, Clock, CheckCircle, XCircle, AlertTriangle, Truck, Battery, Bot, ChevronRight, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Shipment {
  id: string;
  device_id: string;
  device_nickname: string;
  name: string;
  description: string | null;
  origin: string;
  destination: string;
  expected_days: number;
  started_at: string;
  expected_arrival: string;
  temperature_min: number | null;
  temperature_max: number | null;
  status: 'active' | 'completed' | 'cancelled';
  created_at: string;
  completed_at: string | null;
  days_elapsed: number;
  event_count: number;
  latest_event_type: string | null;
  latest_event_severity: string | null;
  latest_location: string | null;
}

interface ShipmentEvent {
  id: string;
  event_type: string;
  severity: string;
  location: string | null;
  latitude: number | null;
  longitude: number | null;
  temperature: number | null;
  battery_level: number | null;
  ai_reasoning: string | null;
  created_at: string;
}

interface ShipmentDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  shipment: Shipment;
  onComplete: (id: string) => void;
  onCancel: (id: string) => void;
}

function ShipmentDetailModal({ isOpen, onClose, shipment, onComplete, onCancel }: ShipmentDetailModalProps) {
  const [events, setEvents] = useState<ShipmentEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_shipment_events', {
        p_shipment_id: shipment.id,
        p_limit: 100
      });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error loading shipment events:', error);
    } finally {
      setLoading(false);
    }
  }, [shipment.id]);

  useEffect(() => {
    if (isOpen) {
      loadEvents();
    }
  }, [isOpen, loadEvents]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEventIcon = (eventType: string, severity: string) => {
    const iconClass = "w-5 h-5";
    switch (eventType) {
      case 'created':
        return <Package className={`${iconClass} text-blue-500`} />;
      case 'gps_update':
        return <MapPin className={`${iconClass} text-gray-500`} />;
      case 'ai_analysis':
        return <Bot className={`${iconClass} text-purple-500`} />;
      case 'route_deviation':
        return <AlertTriangle className={`${iconClass} text-red-500`} />;
      case 'no_report':
        return <Clock className={`${iconClass} text-orange-500`} />;
      case 'temperature_alert':
        return <Thermometer className={`${iconClass} text-red-500`} />;
      case 'battery_alert':
        return <Battery className={`${iconClass} text-yellow-500`} />;
      case 'completed':
        return <CheckCircle className={`${iconClass} text-green-500`} />;
      case 'cancelled':
        return <XCircle className={`${iconClass} text-gray-500`} />;
      default:
        return <Clock className={`${iconClass} text-gray-500`} />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 border-red-300';
      case 'warning':
        return 'bg-yellow-100 border-yellow-300';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getEventTypeLabel = (eventType: string) => {
    switch (eventType) {
      case 'created':
        return 'Shipment Created';
      case 'gps_update':
        return 'Location Update';
      case 'ai_analysis':
        return 'AI Analysis';
      case 'route_deviation':
        return 'Route Deviation';
      case 'no_report':
        return 'No Report Alert';
      case 'temperature_alert':
        return 'Temperature Alert';
      case 'battery_alert':
        return 'Battery Alert';
      case 'completed':
        return 'Shipment Completed';
      case 'cancelled':
        return 'Shipment Cancelled';
      default:
        return eventType;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <Truck className="w-4 h-4 mr-1.5" />
            In Transit
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            <CheckCircle className="w-4 h-4 mr-1.5" />
            Completed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            <XCircle className="w-4 h-4 mr-1.5" />
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  const getProgress = () => {
    if (shipment.status !== 'active') return 100;
    const progress = (shipment.days_elapsed / shipment.expected_days) * 100;
    return Math.min(progress, 100);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-traqpack-yellow rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-gray-900" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{shipment.name}</h2>
                <p className="text-sm text-gray-500">{shipment.device_nickname}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusBadge(shipment.status)}
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Shipment Details */}
          <div className="p-6 border-b border-gray-200">
            {shipment.description && (
              <p className="text-gray-600 mb-4">{shipment.description}</p>
            )}

            {/* Route */}
            <div className="flex items-center text-gray-700 mb-4">
              <MapPin className="w-5 h-5 text-green-500 mr-2" />
              <span className="font-medium">{shipment.origin}</span>
              <ChevronRight className="w-5 h-5 mx-2 text-gray-400" />
              <MapPin className="w-5 h-5 text-red-500 mr-2" />
              <span className="font-medium">{shipment.destination}</span>
            </div>

            {/* Progress for active shipments */}
            {shipment.status === 'active' && (
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span>Day {shipment.days_elapsed} of {shipment.expected_days}</span>
                  <span>ETA: {formatDate(shipment.expected_arrival)}</span>
                </div>
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      getProgress() > 100 ? 'bg-red-500' : 'bg-traqpack-yellow'
                    }`}
                    style={{ width: `${Math.min(getProgress(), 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Details Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-gray-500 mb-1">Started</div>
                <div className="font-medium">{formatDate(shipment.started_at)}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-gray-500 mb-1">Expected</div>
                <div className="font-medium">{formatDate(shipment.expected_arrival)}</div>
              </div>
              {shipment.temperature_min !== null && shipment.temperature_max !== null && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-gray-500 mb-1">Temperature</div>
                  <div className="font-medium">{shipment.temperature_min}°C - {shipment.temperature_max}°C</div>
                </div>
              )}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-gray-500 mb-1">Events</div>
                <div className="font-medium">{shipment.event_count}</div>
              </div>
            </div>

            {/* Note: Actions removed from modal per product decision.
                Complete/Cancel are available on the shipment card only. */}
          </div>

          {/* Events Timeline */}
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Event Timeline</h3>
              <button
                onClick={loadEvents}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-traqpack-yellow"></div>
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No events recorded yet
              </div>
            ) : (
              <div className="space-y-4">
                {events.map((event, index) => (
                  <div
                    key={event.id}
                    className={`relative pl-8 pb-4 ${
                      index < events.length - 1 ? 'border-l-2 border-gray-200' : ''
                    }`}
                  >
                    {/* Timeline dot */}
                    <div className="absolute left-0 top-0 transform -translate-x-1/2 w-8 h-8 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center">
                      {getEventIcon(event.event_type, event.severity)}
                    </div>

                    {/* Event content */}
                    <div className={`ml-4 p-4 rounded-lg border ${getSeverityColor(event.severity)}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">
                          {getEventTypeLabel(event.event_type)}
                        </span>
                        <span className="text-sm text-gray-500">
                          {formatDateTime(event.created_at)}
                        </span>
                      </div>

                      {event.ai_reasoning && (
                        <p className="text-gray-700 mb-2">{event.ai_reasoning}</p>
                      )}

                      {event.location && (
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPin className="w-4 h-4 mr-1" />
                          {event.location}
                        </div>
                      )}

                      {/* Additional data */}
                      <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500">
                        {event.temperature !== null && (
                          <span className="flex items-center">
                            <Thermometer className="w-4 h-4 mr-1" />
                            {event.temperature}°C
                          </span>
                        )}
                        {event.battery_level !== null && (
                          <span className="flex items-center">
                            <Battery className="w-4 h-4 mr-1" />
                            {event.battery_level}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ShipmentDetailModal;
